import { Box, Stack, Typography, Button, Paper } from "@mui/material";
import { errorActions } from "../../store/errorSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { isUserLawEnforcmentAgent } from "../../utils/authUtils";
import { FaCopy, FaDownload } from "react-icons/fa";
import { QueryServicePageFormState } from "./LookupTn";
import { downloadLatestGcmr } from "../../service/lookupTnService";
import logger from "../../utils/logger";
import { LookupTnService } from "../../model/store/lookupTnService";

interface LookupTnResultsProps {
  formState: QueryServicePageFormState;
}

export const LookupTnResults = ({ formState }: LookupTnResultsProps) => {
  const results = useAppSelector((state) => state.lookupTn.data);
  const isLea = useAppSelector((state) =>
    isUserLawEnforcmentAgent(state.auth.userProfile)
  );

  const dispatch = useAppDispatch();

  const onClickDownloadOutput = () => {
    downloadResults(results, formState.queryService);
    dispatch(
      errorActions.setInfo({ errorMessage: "Successfully Downloaded Results" })
    );
  };

  const onClickCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(results, null, 4));
    dispatch(errorActions.setInfo({ errorMessage: "Copied to Clipboard" }));
  };

  const onClickHelp = () => {
    // navigate("/query-service/help");
    //  window.scrollTo({ top: 0, behavior: "smooth" });
    //    window.scrollTo({ top: 0 });
    document
      .getElementById("look-tn-results-info")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onClickDownloadGcmr = async () => {
    let fileDownloadPromise = downloadLatestGcmr();

    let fileDownloadName: string = "NN-GCMR-" + formatDate(new Date()) + ".txt";

    fileDownloadPromise
      .then((res) => {
        if (res.headers["nn-file-name"]) {
          fileDownloadName = res.headers["nn-file-name"];
        } else {
          logger.error(
            "Could NOT find the header: nn-file-name, headers: " +
              JSON.stringify(res.headers)
          );
        }
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileDownloadName);
        link.setAttribute("target", "_blank");

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      })
      .catch((err) => {
        logger.error(err);
      });
  };

  return (
    <>
      <Box className="lookup-tn-results">
        <Stack
          sx={{
            mt: 4,
            flexDirection: {
              lg: "row",
              md: "row",
              sm: "column",
              xs: "column",
            },
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">Results</Typography>
          {isLea ? (
            <Box>
              <Button variant="outlined" sx={{ mr: 4 }} onClick={onClickHelp}>
                Help With Results
              </Button>
              <Button variant="contained" onClick={onClickDownloadGcmr}>
                Download GCMR
              </Button>
            </Box>
          ) : (
            <></>
          )}
        </Stack>
        <Paper elevation={5} className="lookup-tn-results-paper-container">
          {results.length === 0 && (
            <Typography variant="h6">Please enter Numbers first!</Typography>
          )}
          {results.length !== 0 && (
            <>
              <Paper className="lookup-tn-results-floating-actions">
                <Button onClick={onClickCopy}>
                  <FaCopy />
                </Button>
                <Button onClick={onClickDownloadOutput}>
                  <FaDownload />
                </Button>
              </Paper>
              <Box className="lookup-tn-results-json-container">
                <Box className="lookup-tn-results-json-text fixed-width-text">
                  {JSON.stringify(results, null, 4)}
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

const downloadResults = (results: object[], service: LookupTnService | "") => {
  let headers: string[] = [];
  let data: any[][] = [];

  // The key that represents the actual TN may be "tel", or it may be "sip".
  // Because of this, we have to put in logic to determine which to use.
  let possibleTnKeys = ["tel", "sip"];
  let tnKey: string = "";

  // Parse all Headers
  results.forEach((object) => {
    //@ts-ignore
    Object.keys(object).forEach((key) => {
      if (!headers.includes(key)) {
        headers.push(key);
      }
    });
  });

  possibleTnKeys.forEach((possibleKey) => {
    if (tnKey === "" && headers.includes(possibleKey)) {
      tnKey = possibleKey;
    }
  });

  // Set the Tn Key to be the first value inside the headers list
  if (headers.includes(tnKey)) {
    let index = headers.findIndex((key) => key === tnKey);
    headers.splice(index, 1);
    headers.unshift(tnKey);
  }

  results.forEach((object) => {
    let row: any[] = [];
    headers.forEach((key) => {
      // @ts-ignore
      row.push(object[key]);
    });
    data.push(row);
  });

  // Sort by "Number", which should be the first field
  data = data.sort((row1, row2) => row1[0].localeCompare(row2[0]));

  // Add headers to top of the data
  data.unshift(headers);

  let csvContent = "data:text/csv;charset=utf-8,"; // ,sep=" + CSV_FIELD_DELIMITER + ","
  csvContent += data
    .map((row) =>
      row.map((field) => {
        if (field) {
          // Need to call the toString for any times that arent strings yet
          return '"' + field.toString().replaceAll('"', "") + '"';
        } else {
          return "";
        }
      })
    )
    .map((row) => row.join(CSV_FIELD_DELIMITER))
    .join(CSV_LINE_DELIMITER);

  let encodedUri = encodeURI(csvContent);

  const currTimeString = formatDate(new Date());
  const fileName = `Netnumber-Lookup-Export-${lookupServiceToString(
    service
  )}-${currTimeString}.csv`;

  var link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", fileName);
  document.body.appendChild(link); // Required for FF
  link.click();
  document.body.removeChild(link);
};

function formatDate(date: Date) {
  let month =
    date.getMonth() < 9 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
  let date1 = date.getDate() < 9 ? "0" + date.getDate() : date.getDate();
  let hours = date.getHours() < 9 ? "0" + date.getHours() : date.getHours();
  let year = date.getFullYear();

  return year + "" + month + "" + date1 + "" + hours;
}

const CSV_FIELD_DELIMITER = ",";
const CSV_LINE_DELIMITER = "\n";

function lookupServiceToString(service: LookupTnService | ""): string {
  switch (service) {
    case "NCHECK":
      return "Number-Check";
    case "NVIEW":
      return "Number-View";
    case "CID":
      return "Carrier-ID";
    default:
      return "";
  }
}
